import {useApp} from "../../app.context";
import {useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {AddSoundCommand} from "../../commands/addSound.command";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {UpdateSoundCommand} from "../../commands/updateSound.command";
import {SoundMetadata, SoundTypes} from "../../models/remoteConstants.model";
import AudioPlayer from 'react-h5-audio-player';
import '../../styles/audio.css';
import SoundAsset from "../../elements/soundAsset";
import TextArea from "../../elements/textArea.element";
import {RemoveSoundCommand} from "../../commands/removeSound.command";
import BooleanInput from "../../elements/booleanInput.element";
import List from "../../elements/list";
import {downloadFile} from "../../utils/file.utils";
import SoundEditPanel from "../../elements/soundEditPanel";

type SoundDictionary = { [key: string]: SoundMetadata };

function SoundsEditorView() {
    const {state} = useIntegrator();

    function getSoundsByType(type: string): SoundDictionary {
        const dict: SoundDictionary = {};
        for (const id of Object.keys(state.sounds)) {
            const sound = state.sounds[id];
            if (sound.type === type) {
                dict[id] = sound;
            }
        }

        return dict;
    }

    return (
        <div className={'layout horizontal stretch'} style={{gap: 10, flexGrow: 1}}>
            <SoundsCategory title={SoundTypes[0]} sounds={getSoundsByType(SoundTypes[0])}/>
            <SoundsCategory title={SoundTypes[1]} sounds={getSoundsByType(SoundTypes[1])}/>
            <SoundsCategory title={SoundTypes[2]} sounds={getSoundsByType(SoundTypes[2])}/>
        </div>
    )
}

function SoundsCategory(props: {title: string, sounds: SoundDictionary}) {
    const {executeCommand} = useIntegrator();
    const {setNotification} = useApp();
    const [newKey, setNewKey] = useState<string>('');

    function addNewSound() {
        if (newKey === '') {
            setNotification({
                title: 'Invalid Input',
                message: 'Please enter a valid key.'
            })
            return;
        }

        if (props.sounds !== undefined && props.sounds !== null && newKey in props.sounds) {
            setNotification({
                title: 'Key Already Exists',
                message: 'Please enter a new key.'
            })
            return;
        }

        executeCommand(new AddSoundCommand(newKey, props.title));
        setNewKey('');
    }

    return (
        <div className={'layout vertical flex-grow gap'}>
            <List title={props.title} headerClassName={'box'} elements={Object.keys(props.sounds)} component={sound => {
                const value = props.sounds[sound];
                if (value === undefined) {
                    return <></>
                }
                console.log(value)
                return <SoundItem id={sound} metadata={value}/>
            }} search={element => element} keyFor={element => element}/>
            <div className={'layout vertical gap'}>
                <input placeholder={'Enter key...'} value={newKey} onChange={v => setNewKey(v.target.value)}/>
                <button onClick={() => addNewSound()}>Add New Sound</button>
            </div>
        </div>
    )
}

function SoundItem(props: {id: string, metadata: SoundMetadata}) {
    const {executeCommand} = useIntegrator();
    const {getLocalizationValue, getSoundUrl, confirmDelete} = useApp();
    const [editing, setEditing] = useState(false);

    return (
        <div className={'box'} style={{opacity: props.metadata.active ? 1 : 0.5}}>
            <div className={'layout vertical'} style={{gap: 10}}>
                <div className={'layout horizontal stretch'} style={{gap: 6}}>
                    <p className={'flex-grow'}>{getLocalizationValue(props.id)}</p>
                    <BooleanInput value={props.metadata.active}
                                  title={'Active: untick to keep this sound and its file but never play it in game'}
                                  onChange={(value) => {
                        props.metadata.active = value;
                        executeCommand(new UpdateSoundCommand(props.id, props.metadata));
                    }}/>
                    {/* The file now in use, saved as <key>.wav. Earlier versions replaced by an upload are
                        kept in the Archive tab, which can download and restore them. */}
                    <button title={props.metadata.fileName ? 'Download this sound' : 'No file uploaded yet'}
                            disabled={!props.metadata.fileName}
                            onClick={() => {
                                const url = getSoundUrl(props.id);
                                if (url) downloadFile(url, `${props.id}.wav`);
                            }}>
                        <span className={'fa-solid fa-download'}/>
                    </button>
                    <button title={props.metadata.fileName ? 'Trim & pitch' : 'No file uploaded yet'}
                            disabled={!props.metadata.fileName}
                            onClick={() => setEditing(!editing)}>
                        <span className={'fa-solid fa-scissors'}/>
                    </button>
                    <button onClick={async () => {
                        if (await confirmDelete(getLocalizationValue(props.id), 'sound'))
                            executeCommand(new RemoveSoundCommand(props.id));
                    }}>
                        <span className={'fa-solid fa-trash'}/>
                    </button>
                </div>
                <TextArea value={props.metadata.description} placeholder={'Enter description...'} onChange={v => {
                    props.metadata.description = v;
                    executeCommand(new UpdateSoundCommand(props.id, props.metadata));
                }}/>
                <SoundAsset id={props.id} upload={true} style={{width: '100%', height: 60,
                    backgroundColor: 'transparent', border: 'solid 1px var(--overlay-color)'}}
                />
                <AudioPlayer
                    src={getSoundUrl(props.id)}
                    volume={props.metadata.volume / 100}
                    showFilledVolume={true}
                    onVolumeChange={e => {
                        const value = (e.target as any).volume;
                        const volume = Math.round(value * 100);
                        if (volume === props.metadata.volume) {
                            return;
                        }

                        props.metadata.volume = volume;
                        executeCommand(new UpdateSoundCommand(props.id, props.metadata));
                    }}
                    onError={e => console.log(e)}
                />
                {editing && <SoundEditPanel key={props.metadata.fileName} id={props.id} volume={props.metadata.volume}
                                            onDone={() => setEditing(false)}/>}
                <div className={'layout vertical gap'}>
                    <p className={'text header'}>Import Settings</p>
                    {/* Both settings apply to the gameplay ambience effect only (AudioManager.StartAmbientEffect):
                        the data fields keep their old names, startDelay and delay. */}
                    <NumberInputLabel title={'Start Delay'}
                                      tooltip={'Ambient effect only: seconds to wait before it plays the first time.'}
                                      value={props.metadata.startDelay} onChange={v => {
                        props.metadata.startDelay = v;
                        executeCommand(new UpdateSoundCommand(props.id, props.metadata));
                    }}/>
                    <NumberInputLabel title={'Loop Gap'}
                                      tooltip={'Ambient effect only: it loops forever, with this many seconds of silence between the end of one play and the start of the next.'}
                                      value={props.metadata.delay} onChange={v => {
                        props.metadata.delay = v;
                        executeCommand(new UpdateSoundCommand(props.id, props.metadata));
                    }}/>
                </div>
            </div>
        </div>
    )
}

export default SoundsEditorView;